// Second-touch drafts (SECOND-TOUCH-2026-10-02 §4): the "Drafts" list, the panel that
// edits / approves / rejects one draft, and the button on a lead's row that opens it.
//
// Every write goes through lib/drafts.js; App passes the handlers in, so these
// components hold only UI state (the edit buffer, the reject reason, which row is open).
// Nothing here sends on its own: SMS leaves only from "Approve & send" (or "Send now"
// for a draft held in quiet hours), email only from Matt's own mail client.
import { useState, useEffect, useRef, Fragment } from "react";
import { checkDraft, RULE_LABELS, smsSegments, STOP_FOOTER } from "./lib/draftCheck";
import { draftState, STATE_LABELS, rejectedText, sendBlockers, BLOCKER_LABELS, REASON_MAX, redraftBlockers, SOURCE_LABELS, expiryOf, sortForList, isNudge, nudgeBlockers, isReminder, isCandidateDraft, reminderBlockers } from "./lib/drafts";
import { resolveTimezone, withinDraftSendWindow, nextDraftSendTime, sendTimeLabel, STATE_UNKNOWN_NOTE } from "./lib/quietHours";
import { STATE_CODES } from "./lib/leadState";
import { suggestStateFromThread, STATE_NAMES } from "./lib/stateSuggest";
import LocalTimeBadge from "./LocalTimeBadge";

const STATE_STYLE = {
  awaiting: { background: "#dbeafe", color: "#1e40af" },
  failed_check: { background: "#fee2e2", color: "#b91c1c" },
  held: { background: "#fef3c7", color: "#92400e" },
  sent: { background: "#dcfce7", color: "#166534" },
  rejected: { background: "#f1f5f9", color: "#64748b" },
  expired: { background: "#f1f5f9", color: "#64748b" },
  redraft_requested: { background: "#ede9fe", color: "#5b21b6" },
  redrafted: { background: "#f1f5f9", color: "#64748b" },
};
const btn = (bg, fg) => ({ background: bg, color: fg, borderRadius: 7, padding: "6px 11px", fontSize: 12, fontWeight: 600, marginRight: 6 });
const age = (iso, nowMs) => {
  const h = Math.max(0, (nowMs - Date.parse(iso)) / 3600000);
  return h < 1 ? `${Math.round(h * 60)}m` : h < 48 ? `${Math.round(h)}h` : `${(h / 24).toFixed(1)}d`;
};

export function StatePill({ state }) {
  return <span className="pill" data-testid="draft-state" style={STATE_STYLE[state]}>{STATE_LABELS[state]}</span>;
}

/** One draft: the body (editable while it is a draft), its check, and its actions. */
/** "Set state" (OVERNIGHT-AGENTS-2026-10-03 §3): the 50 states + DC, blank allowed; writes leads.state. */
export function SetStateControl({ busy = false, onSet }) {
  const [value, setValue] = useState("");
  return (
    <span data-testid="set-state" style={{ marginRight: 6 }}>
      <select data-testid="set-state-select" value={value} disabled={busy} onChange={(e) => setValue(e.target.value)}
        style={{ fontSize: 12, marginRight: 4 }} aria-label="The lead's state">
        <option value="">State…</option>
        {STATE_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <button className="nb" data-testid="set-state-save" disabled={busy || !value} onClick={() => onSet(value)}
        title="Writes the lead's state; the send window then follows that state's local time."
        style={btn("#fef3c7", "#92400e")}>Set state</button>
    </span>
  );
}

export function DraftPanel({ draft, lead, candidate = null, all = null, nowMs = Date.now(), busy = false, onSave, onApproveSms, onApproveEmail, onMarkSent, onReject, onRedraft,
  onSetState, loadThread, confirmFn = (msg) => window.confirm(msg) }) {
  // hub 2026-10-08: an onboarding nudge belongs to a CANDIDATE, not a lead. Its link is re-read when you approve it.
  // OVERNIGHT-DIRECTSTAFF-2026-10-10 §3: an expiry reminder too; its document is re-read when you approve it.
  const nudge = isNudge(draft);
  const reminder = isReminder(draft);
  if (nudge || reminder) lead = null;
  const state = draftState(draft, nowMs, all);
  const editable = draft.status === "draft" && state !== "expired";
  const [body, setBody] = useState(draft.body);
  const [subject, setSubject] = useState(draft.subject || "");
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const dirty = body !== draft.body || (draft.channel === "email" && subject !== (draft.subject || ""));
  const liveFails = checkDraft(body, draft.channel, draft.channel === "email" ? subject : null, undefined, lead ? lead.discipline : null);
  const blockers = dirty ? ["unsaved"] : nudge ? nudgeBlockers(draft, candidate, null, nowMs)
    : reminder ? reminderBlockers(draft, candidate, null, nowMs) : sendBlockers(draft, lead, nowMs);
  const sendable = blockers.length === 0;
  const isSms = draft.channel === "sms";
  const quiet = isSms && lead && !withinDraftSendWindow(nowMs, lead.state);
  const opensAt = quiet ? nextDraftSendTime(nowMs, lead.state) : null;
  // DINNER-AGENTS-2026-10-02 §3: no state on file (or one that maps to no zone) -> the strictest window,
  // a note on the draft, and a confirm before the text goes. Holding for the window sends nothing, so no confirm.
  const stateUnknown = isSms && lead && !resolveTimezone(lead.state);
  // A4 (OVERNIGHT-MORE-2026-10-03): if the candidate's own reply says where they are, offer that state for one click.
  // Loads this one thread, only while the state is unknown; never writes on its own.
  const [suggested, setSuggested] = useState(null);
  const convId = draft.conversation_id;
  const loadRef = useRef(loadThread);   // the handlers are rebuilt every render; read the latest, fetch once per thread
  loadRef.current = loadThread;
  useEffect(() => {
    let live = true;
    if (!stateUnknown || !convId || !loadRef.current) { setSuggested(null); return undefined; }
    Promise.resolve(loadRef.current(convId))
      .then((msgs) => { if (live) setSuggested(suggestStateFromThread(msgs)); })
      .catch(() => { if (live) setSuggested(null); });
    return () => { live = false; };
  }, [stateUnknown, convId]);
  const approveSms = () => {
    if (stateUnknown && !quiet) {
      if (!confirmFn(`${STATE_UNKNOWN_NOTE}: we do not know where this lead is, so this text is held to 9:00 AM Pacific - 8:00 PM Eastern. Send it now?`)) return;
      return onApproveSms(draft, { confirmUnknownState: true });
    }
    return onApproveSms(draft);
  };

  return (
    <div data-testid="draft-panel" style={{ padding: "12px 0" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, fontSize: 12, color: "#475569" }}>
        <StatePill state={state} />
        {nudge || reminder
          ? <span data-testid="draft-who">Email · {nudge ? "onboarding nudge" : `expiry reminder (${draft.reminder_window}-day)`} · {candidate ? (candidate.name || `candidate ${draft.candidate_id}`) : "candidate not loaded"}</span>
          : <span data-testid="draft-who">{isSms ? "Text" : "Email"} · lead {String(draft.lead_id).slice(0, 8)} · {lead ? (lead.discipline || "-") : "lead not loaded"}</span>}
        {isSms && <span data-testid="segments">{smsSegments(body)} segment(s) · must end "{STOP_FOOTER}"</span>}
        {!isSms && <span data-testid="words">{body.split(/\s+/).filter(Boolean).length} words</span>}
      </div>
      {!isSms && (
        <input data-testid="draft-subject" value={subject} disabled={!editable || busy} onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject" style={{ width: "100%", marginBottom: 6, padding: 6, fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 6 }} />
      )}
      <textarea data-testid="draft-body" value={body} disabled={!editable || busy} onChange={(e) => setBody(e.target.value)} rows={isSms ? 4 : 10}
        style={{ width: "100%", padding: 8, fontSize: 13, fontFamily: "inherit", border: "1px solid #e2e8f0", borderRadius: 6 }} />
      <div data-testid="draft-check" style={{ fontSize: 12, margin: "6px 0", color: liveFails.length ? "#b91c1c" : "#166534" }}>
        {liveFails.length ? "Draft check: " + liveFails.map((f) => RULE_LABELS[f] || f).join("; ") : "Draft check: passes"}
        {dirty && " (unsaved -- Save re-checks it)"}
      </div>
      {draft.status === "sent" && (
        <div style={{ fontSize: 12, color: "#166534" }}>Sent {draft.sent_at ? new Date(draft.sent_at).toLocaleString() : ""}{draft.sent_message_id ? ` · message id ${draft.sent_message_id}` : ""}</div>
      )}
      {state === "rejected" && <div style={{ fontSize: 12, color: "#64748b" }}>Rejected: {rejectedText(draft.rejected_reason)}</div>}
      {state === "redraft_requested" && (
        <div data-testid="draft-redraft-note" style={{ fontSize: 12, color: "#5b21b6" }}>Re-draft requested: the next daily run writes a fresh draft for this lead if the lead still qualifies.</div>
      )}
      {nudge && ["awaiting", "held"].includes(state) && (
        <div data-testid="draft-nudge-note" style={{ fontSize: 12, color: "#475569", margin: "4px 0" }}>
          Goes to the candidate's email. Approving re-checks the onboarding link first: a revoked, expired or completed link is refused.
        </div>
      )}
      {nudge && state === "expired" && (
        <div data-testid="draft-nudge-expired" style={{ fontSize: 12, color: "#64748b" }}>Expired without being sent. While the checklist is still open, the next reminder is drafted automatically.</div>
      )}
      {reminder && ["awaiting", "held"].includes(state) && (
        <div data-testid="draft-reminder-note" style={{ fontSize: 12, color: "#475569", margin: "4px 0" }}>
          Goes to the candidate's email; they reply with a photo or PDF of the renewal -- record it with "Record renewal" on
          the Expirations tab. Approving re-checks the document first: one replaced or re-dated since this draft is refused.
        </div>
      )}
      {reminder && state === "expired" && (
        <div data-testid="draft-reminder-expired" style={{ fontSize: 12, color: "#64748b" }}>Expired without being sent. The fourteen-day reminder is drafted automatically when its window opens.</div>
      )}
      {!nudge && !reminder && state === "expired" && (() => { const rb = redraftBlockers(draft, lead, nowMs); return (
        <span>
          <span style={{ fontSize: 12, color: "#64748b", marginRight: 6 }}>Expired without being sent. Re-draft?</span>
          <button className="nb" data-testid="draft-redraft" disabled={busy || rb.length > 0 || !onRedraft}
            title={rb.length ? rb.map((b) => BLOCKER_LABELS[b] || b).join("; ") : "Asks the next daily run for a fresh draft for this lead, if the lead still qualifies. Sends nothing."}
            onClick={() => onRedraft(draft)} style={btn("#ede9fe", "#5b21b6")}>Re-draft</button>
        </span>
      ); })()}
      {editable && (
        <button className="nb" data-testid="draft-save" disabled={!dirty || busy} onClick={() => onSave(draft, { body, subject, discipline: lead ? lead.discipline : null })} style={btn("#f1f5f9", "#334155")}>Save</button>
      )}
      {lead && ["awaiting", "held"].includes(state) && (
        // §7: beside every send control. A text's quiet-hours lift is already said below ("sendable from"); an email
        // has no window, so the badge carries the time alone.
        <LocalTimeBadge state={lead.state} nowMs={nowMs} />
      )}
      {isSms && ["awaiting", "held"].includes(state) && (
        <button className="nb" data-testid="draft-send" disabled={!sendable || busy || (state === "held" && quiet)}
          title={sendable ? (quiet ? "Inside the lead's quiet hours: approving holds it; nothing is sent until the window opens and you send it." : "Sends this text now from the business line, on the lead's thread.") : blockers.map((b) => BLOCKER_LABELS[b] || b).join("; ")}
          onClick={approveSms} style={btn("#1d4ed8", "white")}>
          {quiet ? (state === "held" ? "Held" : "Approve (hold for the window)") : (state === "held" ? "Send now" : "Approve & send")}
        </button>
      )}
      {stateUnknown && ["awaiting", "held"].includes(state) && (
        <span data-testid="draft-state-unknown" style={{ fontSize: 12, color: "#b45309", fontWeight: 600, marginRight: 6 }}>
          {STATE_UNKNOWN_NOTE} (held to 9:00 AM Pacific - 8:00 PM Eastern)
        </span>
      )}
      {stateUnknown && ["awaiting", "held"].includes(state) && onSetState && (
        <SetStateControl busy={busy} onSet={(v) => onSetState(draft, v)} />
      )}
      {stateUnknown && suggested && ["awaiting", "held"].includes(state) && onSetState && (
        <button className="nb" data-testid="state-suggest" disabled={busy} onClick={() => onSetState(draft, suggested)}
          title="The candidate's own reply names this state. Nothing is set until you click."
          style={btn("#dcfce7", "#166534")}>Reply mentions {STATE_NAMES[suggested]} — set state?</button>
      )}
      {isSms && quiet && opensAt && ["awaiting", "held"].includes(state) && (
        <span data-testid="draft-send-time" style={{ fontSize: 12, color: "#92400e", marginRight: 6 }}>
          Quiet hours where they are -- sendable from {sendTimeLabel(opensAt, lead.state)}
        </span>
      )}
      {/* EMAIL-CRM-2026-10-22: Approve sends it from Matt through crm-mailer (no mail app); unsent after a failure -> Send again or Copy */}
      {!isSms && state === "awaiting" && (
        <button className="nb" data-testid="draft-approve-email" disabled={!sendable || busy}
          title={sendable ? "Sends this subject and message from Matt (his signature is added). It goes on the timeline and the draft is marked sent." : blockers.map((b) => BLOCKER_LABELS[b] || b).join("; ")}
          onClick={() => onApproveEmail(draft)} style={btn("#1d4ed8", "white")}>Approve and send</button>
      )}
      {!isSms && state === "held" && (
        <>
          <button className="nb" data-testid="draft-approve-email" disabled={busy} onClick={() => onApproveEmail(draft)} style={btn("#1d4ed8", "white")}>Send again</button>
          <button className="nb" data-testid="draft-copy-email" disabled={busy} style={btn("#f1f5f9", "#334155")}
            onClick={async () => { try { await navigator.clipboard.writeText(`${draft.subject || ""}\n\n${draft.body || ""}`); } catch (e) { /* the text is on screen */ } }}>Copy</button>
          <button className="nb" data-testid="draft-mark-sent" disabled={busy} title="Only if you sent it some other way" onClick={() => onMarkSent(draft)} style={btn("#166534", "white")}>Mark sent</button>
        </>
      )}
      {["awaiting", "failed_check", "held"].includes(state) && !rejecting && (
        <button className="nb" data-testid="draft-reject" disabled={busy} onClick={() => setRejecting(true)} style={btn("#fee2e2", "#b91c1c")}>Reject</button>
      )}
      {rejecting && (
        <span>
          <input data-testid="draft-reason" value={reason} maxLength={REASON_MAX} onChange={(e) => setReason(e.target.value)}
            placeholder="Why? (required)" style={{ padding: 5, fontSize: 12, width: 260, marginRight: 6, border: "1px solid #e2e8f0", borderRadius: 6 }} />
          <button className="nb" data-testid="draft-reject-confirm" disabled={!reason.trim() || busy} onClick={() => onReject(draft, reason)} style={btn("#b91c1c", "white")}>Reject draft</button>
          <button className="nb" onClick={() => { setRejecting(false); setReason(""); }} style={btn("#f1f5f9", "#334155")}>Cancel</button>
        </span>
      )}
    </div>
  );
}

const FILTERS = { open: ["awaiting", "failed_check", "held", "expired", "redraft_requested"], all: null };

/** The Drafts tab: newest first, open drafts by default, one row opens its panel. */
export function DraftsView({ drafts, leadsById, candidatesById = {}, nowMs = Date.now(), busyId = null, focusId = null, onRefresh, handlers }) {
  const [filter, setFilter] = useState("open");
  const [openId, setOpenId] = useState(focusId);
  // OVERNIGHT-AGENTS-MORE-2026-10-04 §6: live drafts expiring within 48 h first, soonest first; then newest first.
  const rows = sortForList((drafts || []).filter((d) => !FILTERS[filter] || FILTERS[filter].includes(draftState(d, nowMs, drafts))), nowMs);
  return (
    <div className="card" data-testid="drafts-view">
      {/* NAV-2026-10-22: reached from Needs a human on a phone -- the header wraps, the table scrolls inside the card */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap" }}>
        <b style={{ fontSize: 15 }}>Drafts</b>
        <span style={{ fontSize: 12, color: "#64748b" }}>Written by the daily agent; nothing is sent until you send it. No bulk approve.</span>
        <select aria-label="Which drafts" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ marginLeft: "auto", fontSize: 12, maxWidth: "100%", minHeight: 44 }}>
          <option value="open">Open (awaiting, failed check, held, expired)</option>
          <option value="all">All</option>
        </select>
        {onRefresh && <button className="nb" onClick={onRefresh} style={btn("#f1f5f9", "#334155")}>Refresh</button>}
      </div>
      {rows.length === 0 ? (
        <div data-testid="drafts-empty" style={{ padding: 24, color: "#64748b", fontSize: 13 }}>No drafts {filter === "open" ? "awaiting you" : "yet"}.</div>
      ) : (
        <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ textAlign: "left", color: "#64748b", fontSize: 11 }}>
            <th style={{ padding: "8px 12px" }}>Age</th><th>Lead / candidate</th><th>Discipline</th><th>Channel</th><th>Source</th><th>Expires</th><th>State</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((d) => {
              const nudge = isCandidateDraft(d);   // a nudge or an expiry reminder: a candidate, not a lead
              const lead = nudge ? null : leadsById[d.lead_id];
              const cand = nudge ? candidatesById[d.candidate_id] || null : null;
              const state = draftState(d, nowMs, drafts);
              const exp = expiryOf(d, nowMs);
              return (
                <Fragment key={d.id}>
                  <tr data-testid="draft-row" data-draft-id={d.id} data-state={state} style={{ borderTop: "1px solid #f1f5f9", background: openId === d.id ? "#f8fafc" : "white", opacity: state === "expired" ? 0.55 : 1 }}>
                    <td style={{ padding: "8px 12px" }}>{age(d.created_at, nowMs)}</td>
                    <td data-testid="draft-subject-of">{nudge ? `Candidate · ${cand ? cand.name || d.candidate_id : d.candidate_id}`
                      : `${String(d.lead_id).slice(0, 8)}${lead && lead.form_type ? ` · ${lead.form_type}` : ""}`}</td>
                    <td>{lead ? lead.discipline || "-" : "-"}</td>
                    <td>{d.channel === "sms" ? "Text" : "Email"}</td>
                    <td data-testid="draft-source" title={d.source ? "From the draft's source column" : "Inferred: the source column is not on this row"}>
                      {SOURCE_LABELS[d._source] || "-"}{d._source && !d.source ? " *" : ""}</td>
                    <td data-testid="draft-expires" data-soon={exp.soon ? "1" : "0"} style={exp.soon ? { color: "#b91c1c", fontWeight: 600 } : undefined}>
                      {exp.live ? `${exp.soon ? "!! " : ""}in ${exp.hours < 48 ? `${Math.max(0, Math.round(exp.hours))}h` : `${(exp.hours / 24).toFixed(1)}d`}` : "-"}</td>
                    <td><StatePill state={state} /></td>
                    <td><button className="nb" data-testid="draft-open" onClick={() => setOpenId((v) => (v === d.id ? null : d.id))} style={btn("#f1f5f9", "#1d4ed8")}>{openId === d.id ? "Close" : "Open"}</button></td>
                  </tr>
                  {openId === d.id && (
                    <tr><td colSpan={8} style={{ padding: "0 12px", background: "#f8fafc" }}>
                      <DraftPanel key={d.id + d.status + d.body} draft={d} lead={lead} candidate={cand} all={drafts} nowMs={nowMs} busy={busyId === d.id} {...handlers} />
                    </td></tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

/** On a lead's row: shows that the lead has a second-touch draft, and opens it. */
export function LeadDraftButton({ draft, nowMs = Date.now(), onOpen }) {
  if (!draft) return null;
  const state = draftState(draft, nowMs);
  return (
    <button className="nb" data-testid="lead-draft-button" onClick={() => onOpen(draft.id)}
      title="This lead has a second-touch draft. Open it in Drafts."
      style={{ marginLeft: 6, ...STATE_STYLE[state], borderRadius: 7, padding: "6px 11px", fontSize: 12, fontWeight: 600 }}>
      Second touch: {STATE_LABELS[state]}
    </button>
  );
}
